
import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  History,
  Loader2,
  Plus,
  Search,
  User,
} from 'lucide-react';

import { useSchool } from '../../../context/SchoolContext';
import { academicApi } from '../../academics/services/academicApi';
import FeeReceiptModal from '../components/FeeReceiptModal';
import {
  feesApi,
  getFeesApiErrorMessage,
} from '../services/feesApi';

const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CARD', label: 'Card' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'OTHER', label: 'Other' },
];

const REFERENCE_REQUIRED = new Set([
  'UPI',
  'BANK_TRANSFER',
  'CARD',
  'CHEQUE',
]);

const today = () => {
  const current = new Date();
  current.setMinutes(current.getMinutes() - current.getTimezoneOffset());
  return current.toISOString().slice(0, 10);
};

const money = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
};

const FeeCollectionPage = () => {
  const { schoolId, activeAcademicYear, activeAcademicYearId } = useSchool();
  const [classLevels, setClassLevels] = useState([]);
  const [sections, setSections] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);

  const [searchFilters, setSearchFilters] = useState({
    academicYearId: activeAcademicYearId || '',
    classLevelId: '',
    sectionId: '',
    q: '',
    pendingOnly: true,
    overdue: false,
  });
  const [students, setStudents] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingStudentData, setLoadingStudentData] = useState(false);
  const [activeTab, setActiveTab] = useState('DUES');

  const [lineValues, setLineValues] = useState({});
  const [payment, setPayment] = useState({
    payment_date: today(),
    payment_mode: 'CASH',
    reference_number: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [receiptId, setReceiptId] = useState(null);
  const [initialReceipt, setInitialReceipt] = useState(null);
  const [optionalFeeModal, setOptionalFeeModal] = useState(false);
  const [optionalFees, setOptionalFees] = useState([]);
  const [optionalFeeLoading, setOptionalFeeLoading] = useState(false);
  const [optionalFeeSaving, setOptionalFeeSaving] = useState(false);
  const [selectedOptionalFeeHeadId, setSelectedOptionalFeeHeadId] = useState('');

  useEffect(() => {
    setSearchFilters((current) => ({
      ...current,
      academicYearId: activeAcademicYearId || '',
      classLevelId: '',
      sectionId: '',
    }));
    setSelectedStudent(null);
    setLedger([]);
    setHistory([]);
    setStudents([]);
    setClassLevels([]);
    setSections([]);
  }, [schoolId, activeAcademicYearId]);

  useEffect(() => {
    if (!schoolId || !searchFilters.academicYearId) {
      setClassLevels([]);
      return;
    }

    setLoadingClasses(true);
    academicApi
      .getClassLevels(schoolId, {
        academicYearId: searchFilters.academicYearId,
        status: 'ACTIVE',
      })
      .then(setClassLevels)
      .catch((requestError) =>
        setError(getFeesApiErrorMessage(requestError, 'Unable to load classes.')),
      )
      .finally(() => setLoadingClasses(false));
  }, [schoolId, searchFilters.academicYearId]);

  useEffect(() => {
    if (!schoolId || !searchFilters.classLevelId) {
      setSections([]);
      return;
    }

    setLoadingSections(true);
    academicApi
      .getSections(schoolId, {
        academicYearId: searchFilters.academicYearId,
        classLevelId: searchFilters.classLevelId,
        status: 'ACTIVE',
      })
      .then(setSections)
      .catch((requestError) =>
        setError(getFeesApiErrorMessage(requestError, 'Unable to load sections.')),
      )
      .finally(() => setLoadingSections(false));
  }, [schoolId, searchFilters.academicYearId, searchFilters.classLevelId]);

  useEffect(() => {
    if (!schoolId || !searchFilters.academicYearId) {
      setStudents([]);
      return undefined;
    }

    if (
      searchFilters.q.trim().length < 2 &&
      !searchFilters.classLevelId &&
      !searchFilters.pendingOnly &&
      !searchFilters.overdue
    ) {
      setStudents([]);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      setError('');
      try {
        const response = await feesApi.searchStudents(schoolId, searchFilters);
        if (!cancelled) setStudents(response.items || []);
      } catch (requestError) {
        if (!cancelled) {
          setError(
            getFeesApiErrorMessage(
              requestError,
              'Unable to search students.',
            ),
          );
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [schoolId, searchFilters]);

  const loadStudentData = async (student) => {
    if (!student) return;

    setLoadingStudentData(true);
    setError('');

    try {
      const [ledgerItems, historyItems] = await Promise.all([
        feesApi.getStudentLedger(schoolId, student.id, {
          academicYearId: searchFilters.academicYearId,
          all: true,
        }),
        feesApi.getStudentPaymentHistory(schoolId, student.id, {
          academicYearId: searchFilters.academicYearId,
        }),
      ]);

      const pendingLedger = (ledgerItems || []).filter(
        (item) => Number(item.balance_amount || 0) > 0,
      );
      setLedger(pendingLedger);
      setHistory(historyItems || []);

      const initial = {};
      pendingLedger.forEach((item) => {
        const configuredLateFee = Math.max(
          Number(item.calculated_late_fee || 0) - Number(item.fine_amount || 0),
          0,
        );
        const fullSettlement = Number(item.balance_amount || 0) + configuredLateFee;
        initial[item.id] = {
          selected: true,
          amount: fullSettlement.toFixed(2),
          discount_amount: '0.00',
          discount_reason: '',
          fine_amount: configuredLateFee.toFixed(2),
          fine_reason:
            configuredLateFee > 0
              ? 'Automatic installment late fee as per configured grace-period rule.'
              : '',
        };
      });
      setLineValues(initial);
    } catch (requestError) {
      setError(
        getFeesApiErrorMessage(
          requestError,
          'Unable to load student fee information.',
        ),
      );
    } finally {
      setLoadingStudentData(false);
    }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setActiveTab('DUES');
    setSuccess('');
    await loadStudentData(student);
  };

  const openOptionalFeeModal = async () => {
    if (!selectedStudent || !activeAcademicYearId) return;
    setOptionalFeeModal(true);
    setOptionalFeeLoading(true);
    setSelectedOptionalFeeHeadId('');
    setError('');
    try {
      const rows = await feesApi.getStudentOptionalFees(
        schoolId,
        selectedStudent.id,
        activeAcademicYearId,
      );
      setOptionalFees(rows || []);
    } catch (requestError) {
      setError(
        getFeesApiErrorMessage(
          requestError,
          'Unable to load optional fees for this student.',
        ),
      );
      setOptionalFeeModal(false);
    } finally {
      setOptionalFeeLoading(false);
    }
  };

  const assignOptionalFee = async () => {
    if (!selectedStudent || !selectedOptionalFeeHeadId || !activeAcademicYearId) return;
    setOptionalFeeSaving(true);
    setError('');
    try {
      const response = await feesApi.assignStudentOptionalFee(
        schoolId,
        selectedStudent.id,
        {
          academic_year_id: activeAcademicYearId,
          fee_head_id: selectedOptionalFeeHeadId,
        },
      );
      setSuccess(response?.message || 'Optional fee assigned successfully.');
      setOptionalFeeModal(false);
      await loadStudentData(selectedStudent);
    } catch (requestError) {
      setError(
        getFeesApiErrorMessage(
          requestError,
          'Unable to assign the optional fee.',
        ),
      );
    } finally {
      setOptionalFeeSaving(false);
    }
  };

  const summary = useMemo(() => {
    const base = selectedStudent?.fee_summary || {};
    return {
      gross: Number(base.gross || 0),
      paid: Number(base.paid || 0),
      discount: Number(base.discount || 0),
      fine: Number(base.fine || 0),
      outstanding: Number(base.outstanding || 0),
      overdue: Number(base.overdue || 0),
    };
  }, [selectedStudent]);

  const selectedLines = useMemo(
    () =>
      ledger
        .map((item) => ({ item, value: lineValues[item.id] || {} }))
        .filter(({ value }) => value.selected),
    [ledger, lineValues],
  );

  const totalPaying = useMemo(
    () =>
      selectedLines.reduce(
        (total, { value }) => total + Number(value.amount || 0),
        0,
      ),
    [selectedLines],
  );

  const totalDiscount = useMemo(
    () =>
      selectedLines.reduce(
        (total, { value }) => total + Number(value.discount_amount || 0),
        0,
      ),
    [selectedLines],
  );

  const totalFine = useMemo(
    () =>
      selectedLines.reduce(
        (total, { value }) => total + Number(value.fine_amount || 0),
        0,
      ),
    [selectedLines],
  );

  const updateLine = (ledgerId, key, value) => {
    setLineValues((current) => ({
      ...current,
      [ledgerId]: {
        ...current[ledgerId],
        [key]: value,
      },
    }));
  };

  const validatePayment = () => {
    if (!selectedStudent) return 'Select a student.';
    if (selectedLines.length === 0) return 'Select at least one fee head.';
    if (totalPaying <= 0) return 'Payment amount must be greater than zero.';
    if (!payment.payment_date) return 'Payment date is required.';
    if (!payment.payment_mode) return 'Payment mode is required.';
    if (
      REFERENCE_REQUIRED.has(payment.payment_mode) &&
      !payment.reference_number.trim()
    ) {
      return 'Transaction / reference number is required for the selected payment mode.';
    }

    for (const { item, value } of selectedLines) {
      const amount = Number(value.amount || 0);
      const discount = Number(value.discount_amount || 0);
      const fine = Number(value.fine_amount || 0);
      const allowed = Number(item.balance_amount || 0) + fine - discount;

      if (amount <= 0) {
        return `Enter a payment amount for ${item.fee_head_name}.`;
      }
      if (discount < 0 || fine < 0) {
        return `Discount and fine cannot be negative for ${item.fee_head_name}.`;
      }
      if (discount > 0 && String(value.discount_reason || '').trim().length < 5) {
        return `Enter a discount reason of at least 5 characters for ${item.fee_head_name}.`;
      }
      if (allowed < 0) {
        return `Discount exceeds the balance for ${item.fee_head_name}.`;
      }
      if (amount > allowed) {
        return `Payment exceeds the allowed balance for ${item.fee_head_name}.`;
      }
    }

    return '';
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const validation = validatePayment();
    if (validation) {
      setError(validation);
      return;
    }

    const payload = {
      student: selectedStudent.id,
      academic_year: searchFilters.academicYearId,
      payment_date: payment.payment_date,
      payment_mode: payment.payment_mode,
      reference_number: payment.reference_number.trim(),
      amount_paid: totalPaying.toFixed(2),
      notes: payment.notes.trim(),
      allocations: selectedLines.map(({ item, value }) => ({
        ledger: item.id,
        amount: Number(value.amount || 0).toFixed(2),
        discount_amount: Number(value.discount_amount || 0).toFixed(2),
        discount_reason: String(value.discount_reason || '').trim(),
        fine_amount: Number(value.fine_amount || 0).toFixed(2),
        fine_reason: String(value.fine_reason || '').trim(),
      })),
    };

    setSubmitting(true);
    try {
      const response = await feesApi.collectFee(schoolId, payload);
      const receipt = response?.data?.receipt;
      setSuccess(response?.message || 'Payment recorded successfully.');
      setInitialReceipt(receipt || null);
      setReceiptId(receipt?.id || null);
      setPayment({
        payment_date: today(),
        payment_mode: 'CASH',
        reference_number: '',
        notes: '',
      });
      await loadStudentData(selectedStudent);
    } catch (requestError) {
      setError(
        getFeesApiErrorMessage(
          requestError,
          'Unable to record the payment.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-12">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Collect Fee</h1>
        <p className="mt-1 text-sm text-gray-500">
          The working academic year comes from the global sidebar. Filter students by class, section, pending or overdue status and record payments.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
          {success}
        </div>
      )}

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="font-semibold text-gray-900">Student Search</h2>
        <p className="mt-1 text-xs text-gray-500">
          Working year is global. Class and section filters are dependent.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-sm font-semibold text-blue-700">
            {activeAcademicYear?.year_name || 'No working academic year'}
          </div>

          <select
            value={searchFilters.classLevelId}
            onChange={(event) =>
              setSearchFilters((current) => ({
                ...current,
                classLevelId: event.target.value,
                sectionId: '',
              }))
            }
            disabled={!searchFilters.academicYearId || loadingClasses}
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none disabled:bg-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">
              {loadingClasses
                ? 'Loading classes...'
                : activeAcademicYearId
                  ? 'All classes'
                  : 'No working year'}
            </option>
            {classLevels.map((item) => (
              <option key={item.id} value={item.id}>
                {item.class_name || item.name}
              </option>
            ))}
          </select>

          <select
            value={searchFilters.sectionId}
            onChange={(event) =>
              setSearchFilters((current) => ({
                ...current,
                sectionId: event.target.value,
              }))
            }
            disabled={!searchFilters.classLevelId || loadingSections}
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none disabled:bg-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">
              {loadingSections
                ? 'Loading sections...'
                : searchFilters.classLevelId
                  ? 'All sections'
                  : 'Select class first'}
            </option>
            {sections.map((item) => (
              <option key={item.id} value={item.id}>
                {item.section_name || item.name}
              </option>
            ))}
          </select>

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchFilters.q}
              onChange={(event) =>
                setSearchFilters((current) => ({ ...current, q: event.target.value }))
              }
              placeholder="Name, enrolment, admission..."
              className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={searchFilters.pendingOnly}
              onChange={(event) =>
                setSearchFilters((current) => ({
                  ...current,
                  pendingOnly: event.target.checked,
                }))
              }
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            Pending / partial only
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={searchFilters.overdue}
              onChange={(event) =>
                setSearchFilters((current) => ({
                  ...current,
                  overdue: event.target.checked,
                }))
              }
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            Overdue only
          </label>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm xl:col-span-1">
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">Students</h2>
                <p className="mt-1 text-xs text-gray-500">Select a student to view dues.</p>
              </div>
              {searching && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
            </div>
          </div>

          <div className="max-h-[650px] overflow-y-auto p-3">
            {students.length === 0 ? (
              <div className="flex min-h-48 flex-col items-center justify-center text-center text-gray-500">
                <User className="h-9 w-9 text-gray-300" />
                <p className="mt-3 text-sm">No students found.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {students.map((student) => (
                  <button
                    key={student.id}
                    type="button"
                    onClick={() => selectStudent(student)}
                    className={`w-full rounded-xl border p-4 text-left transition ${
                      selectedStudent?.id === student.id
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-gray-900">
                          {student.full_name}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {student.enrolment_no} • {student.current_academic?.class_name} - {student.current_academic?.section_name}
                        </p>
                      </div>
                      <span className="shrink-0 text-sm font-bold text-orange-600">
                        {money(student.fee_summary?.outstanding)}
                      </span>
                    </div>
                    {Number(student.fee_summary?.overdue || 0) > 0 && (
                      <p className="mt-2 text-xs font-medium text-red-600">
                        Overdue: {money(student.fee_summary.overdue)}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm xl:col-span-2">
          {!selectedStudent ? (
            <div className="flex min-h-[520px] flex-col items-center justify-center text-center text-gray-500">
              <User className="h-12 w-12 text-gray-300" />
              <p className="mt-4 font-semibold text-gray-700">Select a student</p>
              <p className="mt-1 text-sm">The fee ledger and payment history will appear here.</p>
            </div>
          ) : (
            <>
              <div className="border-b border-gray-200 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{selectedStudent.full_name}</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {selectedStudent.enrolment_no} • {selectedStudent.current_academic?.academic_year_name} • {selectedStudent.current_academic?.class_name} - {selectedStudent.current_academic?.section_name}
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 sm:items-end">
                    <button
                      type="button"
                      onClick={openOptionalFeeModal}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs font-semibold text-purple-700 hover:bg-purple-100"
                    >
                      <Plus className="h-3.5 w-3.5" /> Assign Optional Fee
                    </button>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <div className="rounded-lg bg-gray-50 px-3 py-2">
                      <p className="text-xs text-gray-500">Gross</p>
                      <p className="mt-1 text-sm font-bold text-gray-900">{money(summary.gross)}</p>
                    </div>
                    <div className="rounded-lg bg-green-50 px-3 py-2">
                      <p className="text-xs text-green-600">Paid</p>
                      <p className="mt-1 text-sm font-bold text-green-700">{money(summary.paid)}</p>
                    </div>
                    <div className="rounded-lg bg-orange-50 px-3 py-2">
                      <p className="text-xs text-orange-600">Outstanding</p>
                      <p className="mt-1 text-sm font-bold text-orange-700">{money(summary.outstanding)}</p>
                    </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex border-b border-gray-200 px-5">
                <button
                  type="button"
                  onClick={() => setActiveTab('DUES')}
                  className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium ${
                    activeTab === 'DUES'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Pending Dues
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('HISTORY')}
                  className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium ${
                    activeTab === 'HISTORY'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500'
                  }`}
                >
                  <History className="h-4 w-4" />
                  Payment History
                </button>
              </div>

              {loadingStudentData ? (
                <div className="flex min-h-80 flex-col items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <p className="mt-3 text-sm text-gray-500">Loading fee details...</p>
                </div>
              ) : activeTab === 'HISTORY' ? (
                <div className="overflow-x-auto p-5">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-left">Receipt</th>
                        <th className="px-4 py-3 text-left">Mode</th>
                        <th className="px-4 py-3 text-left">Reference</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-12 text-center text-gray-500">No payment history found.</td>
                        </tr>
                      ) : (
                        history.map((item) => (
                          <tr key={item.payment_id} className="border-t border-gray-100">
                            <td className="px-4 py-3 text-gray-600">{formatDate(item.payment_date)}</td>
                            <td className="px-4 py-3 font-medium text-gray-900">{item.receipt_no || '—'}</td>
                            <td className="px-4 py-3 text-gray-600">{item.payment_mode}</td>
                            <td className="px-4 py-3 text-gray-600">{item.reference_number || '—'}</td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-900">{money(item.amount_paid)}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                disabled={!item.receipt_id}
                                onClick={() => {
                                  setInitialReceipt(null);
                                  setReceiptId(item.receipt_id);
                                }}
                                className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                              >
                                Receipt
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <form onSubmit={submitPayment} className="p-5">
                  {ledger.length === 0 ? (
                    <div className="rounded-xl bg-green-50 p-6 text-center text-sm text-green-700">
                      This student has no pending fee balance for the selected academic year.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {ledger.map((item) => {
                        const value = lineValues[item.id] || {};
                        return (
                          <div key={item.id} className={`rounded-xl border p-4 ${value.selected ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200'}`}>
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={Boolean(value.selected)}
                                onChange={(event) => updateLine(item.id, 'selected', event.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                  <div>
                                    <p className="font-semibold text-gray-900">
                                      {item.fee_head_name}
                                      {item.fee_installment_name ? ` — ${item.fee_installment_name}` : ''}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-500">
                                      {item.category_name} • Due {formatDate(item.due_date)}
                                      {Number(item.grace_period_days || 0) > 0 ? ` • ${item.grace_period_days} day grace` : ''}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-500">
                                      Base {money(item.amount)}
                                      {Number(item.increase_amount || 0) > 0
                                        ? ` + fee increase ${money(item.increase_amount)}`
                                        : ''}
                                      {' • '}Gross {money(item.gross_amount)}
                                      {' • '}Paid {money(item.paid_amount)}
                                      {' • '}Discount {money(item.discount_amount)}
                                    </p>
                                    {Array.isArray(item.adjustments) && item.adjustments.length > 0 && (
                                      <div className="mt-2 space-y-1 rounded-lg border border-gray-200 bg-white/70 p-2">
                                        {item.adjustments.slice(-4).map((adjustment) => (
                                          <p key={adjustment.id} className="text-xs text-gray-600">
                                            <span className="font-medium text-gray-800">
                                              {adjustment.adjustment_type === 'FEE_INCREASE'
                                                ? 'Fee increase'
                                                : adjustment.adjustment_type === 'DISCOUNT'
                                                  ? 'Discount'
                                                  : 'Fine'}
                                            </span>
                                            {' '}{money(adjustment.amount)} • {adjustment.reason || 'No reason'}
                                          </p>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-500">Balance</p>
                                    <p className={`font-bold ${item.is_overdue ? 'text-red-600' : 'text-orange-600'}`}>
                                      {money(item.balance_amount)}
                                    </p>
                                  </div>
                                </div>

                                {value.selected && (
                                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    <label>
                                      <span className="text-xs font-medium text-gray-600">Pay Now *</span>
                                      <input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={value.amount ?? ''}
                                        onChange={(event) => updateLine(item.id, 'amount', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                      />
                                    </label>
                                    <label>
                                      <span className="text-xs font-medium text-gray-600">Discount Now</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={value.discount_amount ?? ''}
                                        onChange={(event) => updateLine(item.id, 'discount_amount', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                      />
                                    </label>
                                    <label>
                                      <span className="text-xs font-medium text-gray-600">Fine Now</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={value.fine_amount ?? ''}
                                        onChange={(event) => updateLine(item.id, 'fine_amount', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                      />
                                    </label>
                                  </div>
                                )}

                                {value.selected && Number(value.discount_amount || 0) > 0 && (
                                  <label className="mt-3 block">
                                    <span className="text-xs font-medium text-gray-600">Discount Reason *</span>
                                    <input
                                      value={value.discount_reason ?? ''}
                                      maxLength={500}
                                      onChange={(event) => updateLine(item.id, 'discount_reason', event.target.value)}
                                      placeholder="Mandatory reason for this discount"
                                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                    />
                                    <span className="mt-1 block text-xs text-gray-500">Every discount is stored as a separate audited adjustment. Payment plus discount cannot exceed the current balance.</span>
                                  </label>
                                )}

                                {value.selected && Number(value.fine_amount || 0) > 0 && (
                                  <label className="mt-3 block">
                                    <span className="text-xs font-medium text-gray-600">Fine Reason (Optional)</span>
                                    <input
                                      value={value.fine_reason ?? ''}
                                      maxLength={500}
                                      onChange={(event) => updateLine(item.id, 'fine_reason', event.target.value)}
                                      placeholder="Reason for applying the fine"
                                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                    />
                                  </label>
                                )}

                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {ledger.length > 0 && (
                    <div className="mt-6 space-y-5 border-t border-gray-200 pt-6">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <label>
                          <span className="text-sm font-medium text-gray-700">Payment Date <span className="text-red-500">*</span></span>
                          <input
                            type="date"
                            value={payment.payment_date}
                            onChange={(event) => setPayment((current) => ({ ...current, payment_date: event.target.value }))}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          />
                        </label>
                        <label>
                          <span className="text-sm font-medium text-gray-700">Payment Mode <span className="text-red-500">*</span></span>
                          <select
                            value={payment.payment_mode}
                            onChange={(event) => setPayment((current) => ({ ...current, payment_mode: event.target.value }))}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          >
                            {PAYMENT_MODES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                          </select>
                        </label>
                        <label>
                          <span className="text-sm font-medium text-gray-700">
                            Transaction / Reference No. {REFERENCE_REQUIRED.has(payment.payment_mode) && <span className="text-red-500">*</span>}
                          </span>
                          <input
                            value={payment.reference_number}
                            onChange={(event) => setPayment((current) => ({ ...current, reference_number: event.target.value }))}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Transaction, cheque, or reference number"
                          />
                        </label>
                        <label>
                          <span className="text-sm font-medium text-gray-700">Remarks (Optional)</span>
                          <input
                            value={payment.notes}
                            onChange={(event) => setPayment((current) => ({ ...current, notes: event.target.value }))}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          />
                        </label>
                      </div>

                      <div className="rounded-xl bg-gray-50 p-4">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between text-gray-600"><span>Payment amount</span><span>{money(totalPaying)}</span></div>
                          <div className="flex justify-between text-gray-600"><span>Discount applied now</span><span>{money(totalDiscount)}</span></div>
                          <div className="flex justify-between text-gray-600"><span>Fine applied now</span><span>{money(totalFine)}</span></div>
                          <div className="flex justify-between border-t border-gray-200 pt-3 text-lg font-bold text-gray-900"><span>Cash / amount received</span><span className="text-blue-600">{money(totalPaying)}</span></div>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={submitting || selectedLines.length === 0}
                          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                          {submitting ? 'Submitting Payment...' : 'Submit Payment'}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              )}
            </>
          )}
        </section>
      </div>

      {optionalFeeModal && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Assign Optional Fee</h2>
                <p className="mt-1 text-xs text-gray-500">
                  {selectedStudent?.full_name} • {activeAcademicYear?.year_name}
                </p>
              </div>
              <button type="button" onClick={() => setOptionalFeeModal(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">×</button>
            </div>
            <div className="p-5">
              {optionalFeeLoading ? (
                <div className="flex min-h-32 items-center justify-center text-sm text-gray-500">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading optional fees...
                </div>
              ) : optionalFees.length === 0 ? (
                <div className="rounded-xl bg-gray-50 p-5 text-center text-sm text-gray-500">
                  No optional fee is configured for this student's class and working year.
                </div>
              ) : (
                <div className="space-y-3">
                  {optionalFees.map((head) => (
                    <label
                      key={head.id}
                      className={`flex cursor-pointer items-start justify-between gap-3 rounded-xl border p-4 ${
                        head.is_assigned
                          ? 'cursor-not-allowed border-green-200 bg-green-50'
                          : selectedOptionalFeeHeadId === head.id
                            ? 'border-purple-300 bg-purple-50'
                            : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="optional-fee"
                          value={head.id}
                          disabled={head.is_assigned}
                          checked={selectedOptionalFeeHeadId === head.id}
                          onChange={() => setSelectedOptionalFeeHeadId(head.id)}
                          className="mt-1 h-4 w-4 border-gray-300 text-purple-600"
                        />
                        <div>
                          <p className="font-semibold text-gray-900">{head.name}</p>
                          <p className="mt-1 text-xs text-gray-500">{head.category_name} • {head.frequency}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{money(head.amount)}</p>
                        {head.is_assigned && <p className="mt-1 text-xs font-semibold text-green-700">Assigned</p>}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
              <button type="button" onClick={() => setOptionalFeeModal(false)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700">Cancel</button>
              <button
                type="button"
                disabled={!selectedOptionalFeeHeadId || optionalFeeSaving}
                onClick={assignOptionalFee}
                className="rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {optionalFeeSaving ? 'Assigning...' : 'Assign Fee'}
              </button>
            </div>
          </div>
        </div>
      )}

      <FeeReceiptModal
        open={Boolean(receiptId)}
        onClose={() => {
          setReceiptId(null);
          setInitialReceipt(null);
        }}
        schoolId={schoolId}
        receiptId={receiptId}
        initialReceipt={initialReceipt}
      />
    </div>
  );
};

export default FeeCollectionPage;
